import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

const SearchBar = forwardRef(({
  value = '',
  placeholder = 'Search...',
  onSearch,
  disabled = false,
  debounceTime = 1000,
  className = '',
  containerClassName = 'sm:w-[471px]',
  variant = 'default',
  isDark = false,
  minLength = 3,
}, ref) => {
  const [inputValue, setInputValue] = useState(value);
  const searchInputRef = useRef(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Only search if input is empty or meets the minimum length
    if (value === '' || value.length >= minLength) {
      const timeout = setTimeout(() => {
        onSearch?.(value);
      }, debounceTime);
      setSearchTimeout(timeout);
    }
  };

  const handleClear = () => {
    setInputValue('');
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    onSearch?.('');
    searchInputRef.current?.focus();
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => searchTimeout && clearTimeout(searchTimeout);
  }, [searchTimeout]);

  // Sync with parent value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => searchInputRef.current?.focus()
  }));

  return (
    <div className={`relative ${className}`}>
      <div
        className={`h-[40px] rounded-[12px] px-[16px] py-[6px] flex items-center gap-[10px] w-full ${containerClassName} transition-colors duration-200 border-solid border-[1px] ${
          isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
        }`}
      >
        <div
          className="w-[16px] h-[16px] shrink-0"
          style={{
            backgroundColor: isDark ? '#b3b3b3' : '#072837',
            WebkitMaskImage: "url('/assets/icons/search.svg')",
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskSize: 'contain',
            WebkitMaskPosition: 'center',
            maskImage: "url('/assets/icons/search.svg')",
            maskRepeat: 'no-repeat',
            maskSize: 'contain',
            maskPosition: 'center'
          }}
          aria-hidden="true"
        />
        <input
          type="text"
          className={`flex-1 outline-none border-0 font-normal text-[16px] bg-transparent w-full transition-colors duration-200 ${
            isDark ? 'text-[#b3b3b3] placeholder:text-[#8c8c8c]' : 'text-[#072837] placeholder:text-[#686868]'
          }`}
          ref={searchInputRef}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleSearchChange}
          disabled={disabled}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="w-[16px] h-[16px] shrink-0 flex items-center justify-center cursor-pointer bg-transparent border-0 p-0"
            aria-label="Clear search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M9.41406 8L13.707 3.70703C14.0977 3.31641 14.0977 2.68359 13.707 2.29297C13.3164 1.90234 12.6836 1.90234 12.293 2.29297L8 6.58594L3.70703 2.29297C3.31641 1.90234 2.68359 1.90234 2.29297 2.29297C1.90234 2.68359 1.90234 3.31641 2.29297 3.70703L6.58594 8L2.29297 12.293C1.90234 12.6836 1.90234 13.3164 2.29297 13.707C2.48828 13.9023 2.74414 14 3 14C3.25586 14 3.51172 13.9023 3.70703 13.707L8 9.41406L12.293 13.707C12.4883 13.9023 12.7441 14 13 14C13.2559 14 13.5117 13.9023 13.707 13.707C14.0977 13.3164 14.0977 12.6836 13.707 12.293L9.41406 8Z" fill={isDark ? '#B3B3B3' : '#686868'}/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

export default SearchBar;
